import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { BankPicker } from '@/components/BankPicker'
import {
  createOrder,
  deleteOrder,
  getCurrencies,
  getExchanges,
  getOrder,
  patchOrder,
} from '@/lib/api'
import { t } from '@/i18n'
import { formatDecimalInput, fromDateTimeLocal, multiply, parseAmount, toDateTimeLocal } from '@/lib/format'

const ORDER_STATUSES = [
  ['created', 'ostf.created'],
  ['paid', 'ostf.paid'],
  ['completed', 'ostf.completed'],
  ['cancelled', 'ostf.cancelled'],
  ['appeal', 'ostf.appeal'],
] as const

const RECON_STATUSES = [
  ['pending', 'recon.Pending'],
  ['matched', 'recon.Matched'],
  ['unmatched', 'recon.Unmatched'],
] as const

export function OrderForm() {
  const { orderId } = useParams()
  const isNew = !orderId
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const existing = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => getOrder(orderId!),
    enabled: Boolean(orderId),
  })
  const currencies = useQuery({ queryKey: ['currencies'], queryFn: getCurrencies })
  const exchanges = useQuery({ queryKey: ['exchanges'], queryFn: getExchanges })

  const [occurredAt, setOccurredAt] = useState(toDateTimeLocal())
  const [exchange, setExchange] = useState('binance')
  const [side, setSide] = useState<'buy' | 'sell'>('sell')
  const [asset, setAsset] = useState('USDT')
  const [quantity, setQuantity] = useState('')
  const [price, setPrice] = useState('')
  const [fiat, setFiat] = useState('PLN')
  const [fiatAmount, setFiatAmount] = useState('')
  const [fiatManual, setFiatManual] = useState(false)
  const [bankId, setBankId] = useState<string | null>(null)
  const [counterparty, setCounterparty] = useState('')
  const [externalId, setExternalId] = useState('')
  const [orderStatus, setOrderStatus] = useState('completed')
  const [reconStatus, setReconStatus] = useState('pending')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const order = existing.data
    if (!order) return
    setOccurredAt(toDateTimeLocal(order.occurred_at))
    setExchange(order.exchange_code)
    setSide(order.side)
    setAsset(order.asset_code)
    setQuantity(formatDecimalInput(order.quantity))
    setPrice(formatDecimalInput(order.price))
    setFiat(order.fiat_code)
    setFiatAmount(formatDecimalInput(order.fiat_amount))
    setFiatManual(true)
    setBankId(order.bank_id)
    setCounterparty(order.counterparty_nickname ?? '')
    setExternalId(order.external_order_id ?? '')
    setOrderStatus(order.order_status)
    setReconStatus(order.recon_status)
    setNotes(order.notes ?? '')
  }, [existing.data])

  const computed = useMemo(() => multiply(quantity, price), [quantity, price])
  useEffect(() => {
    if (!fiatManual) setFiatAmount(computed ? formatDecimalInput(String(computed)) : '')
  }, [computed, fiatManual])

  const entered = Number(parseAmount(fiatAmount) || 'NaN')
  const roundingDelta = Number.isFinite(entered) ? entered - computed : 0

  const save = useMutation({
    mutationFn: async () => {
      const qty = parseAmount(quantity)
      const unit = parseAmount(price)
      const total = parseAmount(fiatAmount)
      if (!qty || !unit) {
        throw new Error(t('form.needNums'))
      }
      const payload = {
        occurred_at: fromDateTimeLocal(occurredAt),
        exchange_code: exchange,
        side,
        asset_code: asset,
        quantity: qty,
        price: unit,
        fiat_code: fiat,
        fiat_amount: total || null,
        bank_id: bankId,
        counterparty: counterparty || null,
        external_order_id: externalId || null,
        order_status: orderStatus,
        recon_status: reconStatus,
        notes: notes || null,
      }
      return isNew ? createOrder(payload) : patchOrder(orderId as string, payload)
    },
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      void queryClient.invalidateQueries({ queryKey: ['overview'] })
      void queryClient.invalidateQueries({ queryKey: ['freshness'] })
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      void queryClient.invalidateQueries({ queryKey: ['inventory'] })
      void queryClient.invalidateQueries({ queryKey: ['pnl'] })
      navigate(`/orders/${order.id}`)
    },
    onError: (err: unknown) => {
      setError(err instanceof Error ? err.message : t('form.saveFail'))
    },
  })

  const remove = useMutation({
    mutationFn: () => deleteOrder(orderId!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['orders'] })
      navigate('/orders')
    },
  })

  const fiats = (currencies.data ?? []).filter((item) => item.kind === 'fiat')
  const cryptos = (currencies.data ?? []).filter((item) => item.kind === 'crypto')

  return (
    <div className="mx-auto max-w-5xl px-4 py-5 md:px-8 md:py-8">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <Link to="/orders" className="text-xs text-slate-500 hover:text-slate-300">
            ← {t('form.back')}
          </Link>
          <h1 className="mt-1 text-xl font-semibold text-slate-100">{isNew ? t('form.new') : t('form.edit')}</h1>
        </div>
        {!isNew && (
          <button
            type="button"
            className="text-sm text-loss"
            onClick={() => {
              if (window.confirm(t('form.deleteAsk'))) remove.mutate()
            }}
          >
            {t('form.delete')}
          </button>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_280px]">
        <form
          className="space-y-4 rounded-lg border border-surface-border bg-surface-raised p-5"
          onSubmit={(event) => {
            event.preventDefault()
            setError(null)
            save.mutate()
          }}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('form.datetime')}>
              <input
                type="datetime-local"
                value={occurredAt}
                onChange={(event) => setOccurredAt(event.target.value)}
                className={inputClass}
                required
              />
            </Field>
            <Field label={t('common.exchange')}>
              <select value={exchange} onChange={(event) => setExchange(event.target.value)} className={inputClass}>
                {(exchanges.data ?? []).map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('common.side')}>
              <select
                value={side}
                onChange={(event) => {
                  setSide(event.target.value as 'buy' | 'sell')
                  setQuantity((value) => formatDecimalInput(value))
                  setPrice((value) => formatDecimalInput(value))
                  setFiatAmount((value) => formatDecimalInput(value))
                }}
                className={inputClass}
              >
                <option value="sell">{t('form.sellHint')}</option>
                <option value="buy">{t('form.buyHint')}</option>
              </select>
            </Field>
            <Field label={t('common.asset')}>
              <select value={asset} onChange={(event) => setAsset(event.target.value)} className={inputClass}>
                {cryptos.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('common.qty')}>
              <input
                inputMode="decimal"
                value={quantity}
                onChange={(event) => setQuantity(event.target.value)}
                onBlur={() => setQuantity((value) => formatDecimalInput(value))}
                className={`${inputClass} num`}
                required
              />
            </Field>
            <Field label={t('common.price')}>
              <input
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
                onBlur={() => setPrice((value) => formatDecimalInput(value))}
                className={`${inputClass} num`}
                required
              />
            </Field>
            <Field label={t('common.fiat')}>
              <select value={fiat} onChange={(event) => setFiat(event.target.value)} className={inputClass}>
                {fiats.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.code} · {item.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('form.fiatSum')}>
              <input
                inputMode="decimal"
                value={fiatAmount}
                onChange={(event) => {
                  setFiatManual(true)
                  setFiatAmount(event.target.value)
                }}
                onBlur={() => setFiatAmount((value) => formatDecimalInput(value))}
                className={`${inputClass} num`}
              />
            </Field>
          </div>

          <Field label={t('form.bankMethod')}>
            <BankPicker fiat={fiat} value={bankId} onChange={(id) => setBankId(id)} />
          </Field>
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('form.cpNick')}>
              <input value={counterparty} onChange={(event) => setCounterparty(event.target.value)} className={inputClass} />
            </Field>
            <Field label={t('form.extId')}>
              <input value={externalId} onChange={(event) => setExternalId(event.target.value)} className={inputClass} />
            </Field>
            <Field label={t('form.orderStatus')}>
              <select value={orderStatus} onChange={(event) => setOrderStatus(event.target.value)} className={inputClass}>
                {ORDER_STATUSES.map(([id, label]) => (
                  <option key={id} value={id}>
                    {t(label)}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={t('form.reconMoney')}>
              <select value={reconStatus} onChange={(event) => setReconStatus(event.target.value)} className={inputClass}>
                {RECON_STATUSES.map(([id, label]) => (
                  <option key={id} value={id}>
                    {t(label)}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field label={t('form.note')}>
            <textarea value={notes} onChange={(event) => setNotes(event.target.value)} className={`${inputClass} min-h-20`} />
          </Field>
          {error && <p className="text-sm text-loss">{error}</p>}
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded-md bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 disabled:opacity-50"
          >
            {save.isPending ? t('common.saving') : t('common.save')}
          </button>
        </form>

        <aside className="h-fit rounded-lg border border-surface-border bg-surface-raised p-5">
          <h2 className="text-sm font-medium text-slate-300">{t('form.calc')}</h2>
          <dl className="mt-3 space-y-2 text-sm">
            <Row
              label={t('form.qtyPrice')}
              value={`${computed ? formatDecimalInput(String(computed)) : '—'} ${fiat}`}
            />
            <Row label={t('form.entered')} value={`${fiatAmount || '—'} ${fiat}`} />
            <Row
              label={t('form.round')}
              value={`${roundingDelta.toFixed(2)} ${fiat}`}
              warn={Math.abs(roundingDelta) > 1}
            />
            <Row label={t('form.dir')} value={side === 'sell' ? t('form.dirSell') : t('form.dirBuy')} />
          </dl>
          <p className="mt-4 text-xs text-slate-600">
            {t('form.fifoHint')}
          </p>
        </aside>
      </div>
    </div>
  )
}

const inputClass = 'mt-1 w-full rounded-md border border-surface-border bg-surface px-3 py-2 text-sm text-slate-100'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block text-xs text-slate-400">
      {label}
      {children}
    </label>
  )
}

function Row({ label, value, warn }: { label: string; value: string; warn?: boolean }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className={['num', warn ? 'text-pending' : 'text-slate-200'].join(' ')}>{value}</dd>
    </div>
  )
}
